import { Clock, KeyRound, Radio, UserPlus, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { StatCard } from "@/components/ui/stat-card";
import { ChartCard } from "@/components/ui/chart-card";
import { LoadingState } from "@/components/ui/loading-state";
import { KpiPeriod } from "@/models/org-kpis";
import { formatters } from "@/utils/formatters";
import { useOrgActivity } from "../hooks/useOrgActivity";

interface ActivityTabProps {
  organizationId: string;
  period: KpiPeriod;
}

/** Days since a date, for the "gone quiet" signal. */
const daysSince = (iso: string | null) =>
  iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000) : null;

export function ActivityTab({ organizationId, period }: ActivityTabProps) {
  const { data, isLoading, error, refetch } = useOrgActivity(organizationId, period);

  if (isLoading || error || !data) {
    return (
      <LoadingState
        isLoading={isLoading}
        error={error}
        loadingText="Loading activity..."
        errorTitle="Failed to load activity"
        onRetry={() => refetch()}
      />
    );
  }

  const quietDays = daysSince(data.lastInboundAt);
  const { features } = data;
  const featureRows: { label: string; value: string; used: boolean }[] = [
    {
      label: "Bots",
      value: `${features.publishedBots} published of ${features.bots}`,
      used: features.publishedBots > 0
    },
    {
      label: "Campaigns",
      value: `${features.campaignsInPeriod} in period, ${features.campaignsTotal} ever`,
      used: features.campaignsTotal > 0
    },
    {
      label: "WhatsApp Flows",
      value: `${formatters.formatNumber(features.flowSubmissions)} submissions in period`,
      used: features.flowSubmissions > 0
    },
    {
      label: "Data Hub",
      value: `${features.dataHubTables} tables`,
      used: features.dataHubTables > 0
    },
    {
      label: "Public API",
      value: features.apiKeys
        ? `${features.apiKeys} active keys, ${formatters.formatNumber(features.apiKeyUsage)} calls${
            features.apiKeyLastUsedAt
              ? `, last ${formatters.formatRelativeTime(features.apiKeyLastUsedAt)}`
              : ""
          }`
        : "No active keys",
      used: features.apiKeys > 0
    },
    { label: "Team", value: `${features.members} members`, used: features.members > 1 }
  ];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Last customer message"
          value={data.lastInboundAt ? formatters.formatRelativeTime(data.lastInboundAt) : "Never"}
          hint={quietDays !== null && quietDays >= 7 ? `Quiet for ${quietDays} days` : undefined}
          icon={Clock}
          tone={quietDays === null || quietDays >= 7 ? "warning" : "default"}
        />
        <StatCard
          label="Last message sent"
          value={data.lastOutboundAt ? formatters.formatRelativeTime(data.lastOutboundAt) : "Never"}
          icon={Clock}
        />
        <StatCard
          label="Contacts active"
          value={formatters.formatNumber(data.activeContacts)}
          hint="Conversations with a message in the period"
          icon={Users}
        />
        <StatCard
          label="New contacts"
          value={formatters.formatNumber(data.newContacts)}
          hint="First seen in the period"
          icon={UserPlus}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard title="Channels">
          <ul className="space-y-2 text-sm">
            {data.channels.map((c) => (
              <li key={c.channelId} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2">
                  <Radio className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{c.name}</span>
                  <span className="text-xs text-muted-foreground">{c.type.toLowerCase()}</span>
                </span>
                <span className="flex items-center gap-2">
                  {c.botName && <span className="text-xs text-muted-foreground">{c.botName}</span>}
                  {c.connected && c.active ? (
                    <Badge variant="success">Live</Badge>
                  ) : c.connected ? (
                    <Badge variant="outline">Paused</Badge>
                  ) : (
                    <Badge variant="destructive">Disconnected</Badge>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </ChartCard>
        <ChartCard title="Features in use" description="Where to look when helping them grow">
          <ul className="space-y-2 text-sm">
            {featureRows.map((f) => (
              <li key={f.label} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2">
                  <span
                    className={`h-2 w-2 rounded-full ${f.used ? "bg-chart-2" : "bg-muted-foreground/30"}`}
                  />
                  {f.label}
                </span>
                <span className="text-muted-foreground">{f.value}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
            <KeyRound className="h-3 w-3" /> API call counts are lifetime totals.
          </p>
        </ChartCard>
      </div>
    </div>
  );
}
